import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, filter, map, Observable } from 'rxjs';

import { PageContextQueryService, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { PageInfo } from '@blaze-shared/api-contracts-domain';
import { PageContext, PageContextOrigin } from '@blaze-shared/projects-domain';
import { countedShareReplay } from '@blaze-shared/shared-util';

export interface BreadcrumbsPathItem {
  label: string;
  pageContext?: PageContext;
  pageId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbsResolverService {
  private readonly pageContextSelector = inject(PageContextQueryService);
  private readonly store = inject(Store);

  readonly drilldownBreadcrumbs$: Observable<BreadcrumbsPathItem[]>;

  constructor() {
    const loadedPageId$ = combineLatest([
      this.store.select(runtimeSelectors.selectPageId),
      this.store.select(runtimeSelectors.selectLayoutIds),
    ]).pipe(
      filter(([currentPageId, layoutIds]) => {
        return currentPageId ? layoutIds.includes(currentPageId) : false;
      }),
      map(([currentPageId]) => currentPageId),
      distinctUntilChanged(),
      countedShareReplay(),
    );

    this.drilldownBreadcrumbs$ = combineLatest([
      this.pageContextSelector.getAllPageContexts(),
      this.pageContextSelector.getCurrentPageContext(),
      this.store.select(runtimeSelectors.selectPages),
      loadedPageId$,
    ]).pipe(
      map(([allContexts, context, pages, currentPageId]) => {
        const currentPageContext = context?.pageId === currentPageId ? context : undefined;
        const originPath = this.traverseOrigins(
          currentPageContext?.origin,
          Object.values(allContexts),
          pages,
        );

        const currentPage = pages.find((p) => p.id === currentPageId);
        const currentPageLabel = currentPage?.name || currentPage?.urlSlug || 'Unnamed';
        const currentPageBreadcrumb = { label: currentPageLabel, pageId: undefined };

        return [...originPath, currentPageBreadcrumb];
      }),
      countedShareReplay(),
    );
  }

  private traverseOrigins(
    origin: PageContextOrigin | undefined | null,
    allContexts: PageContext[],
    pages: PageInfo[],
  ): BreadcrumbsPathItem[] {
    const path: BreadcrumbsPathItem[] = [];

    let currentOrigin = origin;

    while (currentOrigin) {
      const context = allContexts.find((ctx) => ctx.id === currentOrigin?.pageContextId);
      if (currentOrigin.pageId) {
        const page = pages.find((p) => p.id === currentOrigin?.pageId);
        const label = page?.name || page?.urlSlug || 'Unnamed';
        path.unshift({
          label,
          pageContext: context,
          pageId: currentOrigin.pageId,
        });
      }

      if (context) {
        currentOrigin = context.origin;
      } else {
        break;
      }
    }

    return path;
  }
}
